import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  getSession,
  useGetAllOnlineUsersQuery,
  useGetAllProfileQuery,
  useGetCommunityStats,
} from '@onesource/services';
import { useMemo, useState, type FC, type ReactNode } from 'react';
import { HiBars3, HiOutlineUsers } from 'react-icons/hi2';

import { CommunityDetailsDrawer } from './community-details-drawer';

const REFRESH_INTERVAL_MS = 30 * 1000;

/** 1234 → "1.2k"; keeps the bar short for large member counts. */
const compactNumber = (value: number): string =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const StatItem: FC<{ icon: ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
    {icon}
    <Typography
      component="span"
      noWrap
      sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12.5, color: 'rgba(255,255,255,0.82)' }}
    >
      <Box component="span" sx={{ fontWeight: 700, color: 'common.white' }}>
        {value}
      </Box>{' '}
      {label}
    </Typography>
  </Box>
);

interface BannerStatsBarProps {
  spaceId: string;
  /** Currently applied feed tag-filter values — forwarded to the details drawer. */
  selectedTagValues?: string[];
  /** Toggles a tag in the feed's tag filter — forwarded to the details drawer. */
  onTagClick: (value: string) => void;
  /**
   * `overlay` (default) pins the bar across the bottom of the banner as frosted
   * glass; `below` renders it as a solid strip under the banner, for banners
   * shown as full images whose artwork must not be covered.
   */
  placement?: 'overlay' | 'below';
}

/**
 * A frosted-glass stat strip pinned across the bottom of the space banner: the
 * banner image stays visible (blurred) behind it, so it reads as part of the
 * banner rather than a separate card. It's the mobile stand-in for the desktop
 * sidebar's stats + who's-online cards — member count and how many people are
 * online now. Trending tags and the full contributor / online lists stay
 * desktop-only (tags are reachable from the feed filter bar on mobile).
 * Renders nothing until it has a number, so the banner never gains an empty bar.
 */
export const BannerStatsBar: FC<BannerStatsBarProps> = ({
  spaceId,
  selectedTagValues,
  onTagClick,
  placement = 'overlay',
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const currentUserId = profile?.id?.toString();

  const { data: stats } = useGetCommunityStats(spaceId);
  const { data: onlineData } = useGetAllOnlineUsersQuery({
    enabled: !!token,
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  // Match the desktop Who's Online count: drop private users and self, de-dupe.
  const onlineCount = useMemo(() => {
    const seen = new Set<string>();
    return (onlineData?.users ?? []).filter(user => {
      if (user.isPrivate || user.id === currentUserId || seen.has(user.id)) {
        return false;
      }
      seen.add(user.id);
      return true;
    }).length;
  }, [onlineData?.users, currentUserId]);

  if (!stats && onlineCount === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ...(placement === 'overlay'
          ? {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              // Frosted glass over the banner image — image shows through, blurred.
              bgcolor: 'rgba(0,0,0,0.34)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }
          : {
              // Solid strip flush under a full-image banner (nothing to frost);
              // the parent clips both to one rounded shape.
              bgcolor: 'rgba(0,0,0,0.6)',
            }),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 0.85,
        ...(placement === 'overlay' ? { borderBottomLeftRadius: '11px', borderBottomRightRadius: '11px' } : {}),
      }}
    >
      {stats && (
        <StatItem
          icon={<HiOutlineUsers size={14} color="rgba(255,255,255,0.85)" />}
          value={compactNumber(stats.totalMembers)}
          label="members"
        />
      )}
      {onlineCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#37d187' }} />
          <Typography
            sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12.5, fontWeight: 600, color: 'common.white' }}
          >
            {onlineCount} online
          </Typography>
        </Box>
      )}

      {/* Right-aligned trigger — opens the full community sheet */}
      <Box
        component="button"
        type="button"
        onClick={() => setDetailsOpen(true)}
        sx={{
          ml: 'auto',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          border: 'none',
          cursor: 'pointer',
          borderRadius: '999px',
          pl: 1,
          pr: 1.25,
          py: 0.5,
          bgcolor: 'rgba(255,255,255,0.18)',
          color: 'common.white',
          fontFamily: 'Figtree, sans-serif',
          fontSize: 12.5,
          fontWeight: 700,
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
        }}
      >
        <HiBars3 size={15} />
        More
      </Box>

      <CommunityDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        spaceId={spaceId}
        selectedTagValues={selectedTagValues}
        onTagClick={onTagClick}
      />
    </Box>
  );
};
